import {
    Box,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    FormHelperText,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import { AddRuleRequest } from 'types/rule/rule';
import {
    useAddRuleMutation,
    useUpdateRuleMutation,
} from 'store/slices/rule/add-rule';
import pxToRem from 'assets/theme/function/px-to-rem';
import {
    useEntitiesListQuery,
    useOperatorListQuery,
    useLazyRuleIdListQuery,
} from 'store/slices/rule/rule-entities-enum';
import borders from 'assets/theme/base/borders';
import { QueryBuilderMaterial } from '@react-querybuilder/material';
import { QueryBuilder, type RuleGroupType } from 'react-querybuilder';
import Fields from 'pages/product/rule/fields';
import { ValueEditor } from 'react-querybuilder';
import AutocompleteValueEditor from './components/autocomplete';
import { useParams } from 'react-router-dom';
import SuccessSnackbarEffect from 'component/success-snackbar';
import BackNavigate from 'component/back-navigate/back-navigate';

const useOperator = () => {
    const { data: operatorList } = useOperatorListQuery();

    return (
        operatorList?.map((operator) => ({
            label: operator,
            value: operator,
            name: operator,
        })) || []
    );
};

const AddRule: React.FC = () => {
    const {
        handleSubmit,
        reset,
        control,
        formState: { errors },
    } = useForm<AddRuleRequest>();
    const initialQuery: RuleGroupType = { combinator: 'and', rules: [] };
    const [query, setQuery] = useState<RuleGroupType>(initialQuery);
    const operators = useOperator();

    const [ruleList, { data: RuleData }] = useLazyRuleIdListQuery();
    const { data: entitiesList } = useEntitiesListQuery();
    const [
        addRule,
        {
            error: addError,
            isError: addIsError,
            isSuccess: addIsSuccess,
            isLoading,
        },
    ] = useAddRuleMutation();
    const [
        updateRule,
        {
            error: updateRuleError,
            isError: updateRuleIsError,
            isSuccess: updateIsSuccess,
        },
    ] = useUpdateRuleMutation();

    const formRef = useRef<HTMLFormElement>(null);
    const dispatch = useAppDispatch();

    const { ruleId } = useParams<{ ruleId: string }>();

    const handleAddGroup = () => {
        const newGroup: RuleGroupType = {
            combinator: 'and',
            rules: [],
        };
        setQuery((prevQuery) => ({
            ...prevQuery,
            rules: [...prevQuery.rules, newGroup],
        }));
    };

    const transformRules = (inputRules: any[]): RuleGroupType['rules'] => {
        return inputRules.map((rule) => {
            if ('combinator' in rule && 'rules' in rule) {
                return {
                    combinator: rule.combinator,
                    rules: transformRules(rule.rules),
                };
            } else {
                return {
                    field: rule.field ?? '',
                    value: rule.value ?? '',
                    operator: rule.operator ?? '',
                    user: rule.user ?? '',
                };
            }
        }) as RuleGroupType['rules'];
    };

    const onSubmit = (values: AddRuleRequest) => {
        const entitiesArray = Array.isArray(values.entities)
            ? values.entities
            : [values.entities];
        if (ruleId) {
            updateRule({
                name: values.name,
                description: values.description,
                entities: entitiesArray,
                value: {
                    combinator: query.combinator ?? 'and',
                    rules: transformRules(query.rules ?? []),
                },
                ruleId: ruleId,
            });
        } else {
            addRule({
                name: values.name,
                description: values.description,
                entities: entitiesArray,
                value: {
                    combinator: query.combinator ?? 'and',
                    rules: transformRules(query.rules ?? []),
                },
            });
        }
    };

    useEffect(() => {
        if (ruleId) {
            ruleList(ruleId);
        }
    }, [ruleId, ruleList]);

    useEffect(() => {
        if (RuleData) {
            reset({
                name: RuleData.name,
                description: RuleData.description,
                entities: RuleData.entities,
            });

            setQuery({
                combinator: RuleData.value.combinator || 'and',
                rules: transformRules(RuleData.value.rules || []),
            });
        }
    }, [RuleData, reset]);

    useEffect(() => {
        const error = addError || updateRuleError;

        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [addIsError, updateRuleIsError]);

    return (
        <>
            <Box mb={pxToRem(10)} display={'grid'} alignItems={'center'}>
                <BackNavigate navigateUrl={'/product/rule'} />
                <Typography variant="h5" pl={pxToRem(10)}>
                    {ruleId ? 'Edit Rule Condition' : 'Create Rule Condition'}
                </Typography>
            </Box>

            <Box
                ref={formRef}
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                display={'flex'}
            >
                <Controller
                    name="name"
                    control={control}
                    rules={{
                        required: 'Card Name is required',
                    }}
                    render={({ field: { name, value = '', onChange } }) => (
                        <TextField
                            sx={{ width: '50%', mb: pxToRem(20) }}
                            name={name}
                            value={value}
                            onChange={onChange}
                            label="Name"
                            error={Boolean(errors[name])}
                            helperText={errors[name]?.message?.toString() || ''}
                        />
                    )}
                />
                <Controller
                    name="description"
                    control={control}
                    rules={{
                        required: 'Card Description is required',
                    }}
                    render={({ field: { name, value = '', onChange } }) => (
                        <TextField
                            sx={{
                                width: '50%',
                                mb: pxToRem(20),
                                ml: pxToRem(10),
                            }}
                            name={name}
                            value={value}
                            onChange={onChange}
                            label="Description"
                            error={Boolean(errors[name])}
                            helperText={errors[name]?.message?.toString() || ''}
                        />
                    )}
                />

                <FormControl
                    error={Boolean(errors?.entities)}
                    sx={{ ml: pxToRem(10), width: '50%' }}
                    fullWidth
                >
                    <InputLabel>Entities</InputLabel>
                    <Controller
                        name="entities"
                        control={control}
                        rules={{
                            required: 'Entities is required',
                        }}
                        render={({ field: { name, value = '', onChange } }) => (
                            <Select
                                label="Entities"
                                name={name}
                                value={value}
                                onChange={onChange}
                            >
                                {entitiesList?.map((category) => (
                                    <MenuItem
                                        key={category.toString()}
                                        value={category.toString()}
                                    >
                                        {category}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                    {errors?.entities && (
                        <FormHelperText>
                            {errors?.entities.message?.toString()}
                        </FormHelperText>
                    )}
                </FormControl>
            </Box>
            <Box
                p={pxToRem(10)}
                mt={pxToRem(5)}
                borderRadius={borders.borderRadius.xl}
                bgcolor={colors.secondary.main}
                border={`1px solid ${rgba(colors.info.main, 0.5)}`}
                sx={{
                    '.ruleGroup': {
                        gap: pxToRem(10),
                        flexWrap: 'wrap',
                        border: `1px solid ${rgba(colors.common.white, 0.5)}`,
                        padding: pxToRem(10),
                        margin: pxToRem(5),
                        borderRadius: pxToRem(8),
                    },
                    '.ruleGroup .rule': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: pxToRem(10),
                        margin: pxToRem(10),
                        padding: pxToRem(10),
                        flexWrap: 'wrap',
                        backgroundColor: rgba(colors.success.main, 0.1),
                        opacity: 0.9,
                        borderRadius: borders.borderRadius.md,
                    },
                    '.rule .rule-field, .rule .rule-operator, .rule .rule-value':
                        {
                            minWidth: pxToRem(200),
                            padding: pxToRem(5),
                        },
                    '.rule .rule-remove': {
                        marginLeft: pxToRem(10),
                    },

                    '.ruleGroup .ruleGroup-addRule, .ruleGroup .ruleGroup-addGroup':
                        {
                            margin: pxToRem(10),
                            textAlign: 'center',
                        },
                }}
            >
                <QueryBuilderMaterial>
                    <QueryBuilder
                        fields={Fields()}
                        query={query}
                        onQueryChange={setQuery}
                        addRuleToNewGroups={true}
                        getOperators={() => operators}
                        controlElements={{
                            addGroupAction: (props) => {
                                const {
                                    testID,
                                    handleOnClick,
                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                    ruleOrGroup: ruleOrGroup,
                                    ...buttonProps
                                } = props;
                                const isRootGroup = props.level !== 1;

                                return (
                                    <Button
                                        data-testid={testID}
                                        variant={'contained'}
                                        size={'small'}
                                        {...buttonProps}
                                        disabled={!isRootGroup}
                                        onClick={
                                            handleOnClick || handleAddGroup
                                        }
                                    >
                                        Add Group
                                    </Button>
                                );
                            },

                            valueEditor: (props) => (
                                <Box minWidth={'30%'}>
                                    {props.field === 'user-ids' ? (
                                        <AutocompleteValueEditor {...props} />
                                    ) : (
                                        <ValueEditor {...props} />
                                    )}
                                </Box>
                            ),
                        }}
                        controlClassnames={{
                            ruleGroup: 'ruleGroup',
                            rule: 'rule',
                            fields: 'rule-field',
                            operators: 'rule-operator',
                            value: 'rule-value',
                            removeRule: 'rule-remove',
                        }}
                    />
                </QueryBuilderMaterial>
            </Box>
            <Box textAlign={'end'} mt={pxToRem(20)}>
                <Button
                    sx={{ width: '20%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                    loading={isLoading}
                >
                    {!ruleId ? 'Create Rule' : 'Update Rule'}
                </Button>
            </Box>

            <SuccessSnackbarEffect
                isSuccess={addIsSuccess || updateIsSuccess}
                message={
                    addIsSuccess
                        ? 'Successfully Created a Rule!'
                        : updateIsSuccess
                          ? 'Successfully Updated the Rule!'
                          : ''
                }
            />
        </>
    );
};

export default AddRule;
